import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Badge from 'antd/lib/badge';
import Button from 'antd/lib/button';
import Divider from 'antd/lib/divider';
import Dropdown from 'antd/lib/dropdown';
import Input from 'antd/lib/input';
import Popover from 'antd/lib/popover';
import Spin from 'antd/lib/spin';
import Tabs from 'antd/lib/tabs';
import {
    AimOutlined,
    CheckSquareOutlined,
    ClockCircleOutlined,
    FilterOutlined,
    LoadingOutlined,
    PlusOutlined,
    RocketOutlined,
    SearchOutlined,
} from '@ant-design/icons';
import { antGrey, colorWithAlpha, runwayColors, rwColors } from '@/components/styles/globalStyles';
import { TriageIssueCard } from './TriageIssueCard';
import { CreateIssueModal } from './CreateIssueModal';
import { TriageFilters } from './TriageFilters';
import { tryFetchPaginatedEntities } from '@/actions/entities';
import {
    setTriageReleaseFilter,
    setTriageSearch,
    setTriageSortBy,
    setTriageStatusGroup,
    TriageReleaseFilter,
    TriageSortBy,
    TriageStatusGroup,
    TriageViewType,
} from '@/actions/triage';
import { FetchableEntityType, FetchablePaginatedEntityType } from '@/entityType';
import { RootState } from '@/reducers/reducers';
import {
    selectAppHome,
    selectAppIssues,
    selectIssuesCounts,
    selectPaginatedIssuesLimitAndOffset,
    selectRelease,
    selectTriageFilters,
} from '@/selectors/selectors';
import { useGetEntity, useIsLoading } from '@/utils/hooks/helpers';
import { AppComponentProps } from '@/typescriptHelpers';

const SORT_OPTIONS: { value: TriageSortBy; label: string }[] = [
    { value: 'triage', label: 'Triage' },
    { value: 'date', label: 'Date' },
    // TODO: 'destinations' sort deferred (needs issue → destination mapping).
    { value: 'owner', label: 'Owner' },
    { value: 'source', label: 'Source' },
];

const sortLabel = (sortBy: TriageSortBy): string => SORT_OPTIONS.find((o) => o.value === sortBy)?.label ?? 'Triage';

const PAGE_SIZE = 20;

const filterButtonTextStyle: React.CSSProperties = { color: antGrey.grey8 };
const filterButtonIconStyle: React.CSSProperties = { color: rwColors.primary };

const tabLabel = (label: string, count: number, isActive: boolean) => (
    <span style={{ fontWeight: 600, fontSize: 16, color: isActive ? rwColors.primary : antGrey.grey7 }}>
        {label}{' '}
        <span style={{ fontWeight: 400, color: isActive ? colorWithAlpha(rwColors.primary, 0.5) : antGrey.grey5 }}>
            - {count}
        </span>
    </span>
);

export const TriageIssuesList: React.FC<AppComponentProps & { releaseId?: string }> = ({ orgId, appId, releaseId }) => {
    const dispatch = useDispatch();

    const [isCreateModalOpen, setIsCreateModalOpen] = React.useState(false);
    const [isFilterPopoverOpen, setIsFilterPopoverOpen] = React.useState(false);

    const filters = useSelector((state: RootState) => selectTriageFilters(state, appId));

    // Local input value drives the field immediately; the committed search (which triggers the refetch) is debounced.
    const [searchInput, setSearchInput] = React.useState(filters.search);
    const searchTimer = React.useRef<ReturnType<typeof setTimeout>>();
    React.useEffect(() => () => clearTimeout(searchTimer.current), []);
    const onSearchChange = (next: string) => {
        setSearchInput(next);
        clearTimeout(searchTimer.current);
        searchTimer.current = setTimeout(() => dispatch(setTriageSearch({ appId, search: next.trim() })), 300);
    };
    const commitSearch = (next: string) => {
        clearTimeout(searchTimer.current);
        dispatch(setTriageSearch({ appId, search: next.trim() }));
    };

    const issues = useSelector((state: RootState) => selectAppIssues(state, appId));
    const { offset } = useSelector((state: RootState) => selectPaginatedIssuesLimitAndOffset(state, appId));

    // Live/Next release versions back the release filter buttons. Ensure appHome is loaded for them.
    const appHome = useSelector((state: RootState) => selectAppHome(state, appId));
    const liveVersion = appHome?.liveRelease?.version;
    const nextVersion = appHome?.nextRelease?.version;
    const [fetchAppHome] = useGetEntity(FetchableEntityType.AppHome, appId);
    React.useEffect(() => {
        fetchAppHome({ orgId, appId });
    }, [orgId, appId]);

    // At the release-level route, always scope to the release we're viewing. Otherwise the Live/Next
    // buttons take precedence, and falling back to the explicit Version filter from the popover.
    const releaseVersion = useSelector((state: RootState) =>
        releaseId ? selectRelease(state, releaseId)?.version : undefined,
    );
    let activeVersions: string[] = filters.versions;

    if (releaseId) {
        activeVersions = releaseVersion ? [releaseVersion] : [];
    } else if (filters.releaseFilter === 'live') {
        activeVersions = liveVersion ? [liveVersion] : [];
    } else if (filters.releaseFilter === 'next') {
        activeVersions = nextVersion ? [nextVersion] : [];
    }

    // The explicit Version filter only applies (and is shown) at app level with no Live/Next selected.
    const isVersionFilterActive = !releaseId && filters.releaseFilter === 'none';

    const appliedFilterCount =
        filters.statuses.length +
        filters.sources.length +
        filters.owners.length +
        filters.reporters.length +
        (isVersionFilterActive ? filters.versions.length : 0);

    const fetchIssues = React.useCallback(
        (statusGroup: TriageStatusGroup, pageOffset: number) => {
            dispatch(
                tryFetchPaginatedEntities(
                    FetchablePaginatedEntityType.Issues,
                    {
                        orgId,
                        appId,
                        statusGroup,
                        statuses: filters.statuses,
                        sources: filters.sources,
                        owners: filters.owners,
                        reporters: filters.reporters,
                        versions: activeVersions,
                        sortBy: filters.sortBy,
                        search: filters.search || undefined,
                    },
                    PAGE_SIZE,
                    pageOffset,
                    appId,
                ),
            );
        },
        [
            dispatch,
            orgId,
            appId,
            filters.statuses,
            filters.sources,
            filters.owners,
            filters.reporters,
            filters.sortBy,
            filters.search,
            activeVersions.join(','),
        ],
    );

    React.useEffect(() => {
        fetchIssues(filters.statusGroup, 0);
    }, [filters.statusGroup, fetchIssues]);

    const toggleReleaseFilter = (target: TriageReleaseFilter) => {
        // Re-clicking the active filter clears it; the two are mutually exclusive.
        dispatch(setTriageReleaseFilter({ appId, releaseFilter: filters.releaseFilter === target ? 'none' : target }));
    };

    const releaseFilterButtonStyle = (active: boolean): React.CSSProperties => ({
        color: active ? runwayColors.white : antGrey.grey8,
        backgroundColor: active ? rwColors.primary : runwayColors.white,
        borderColor: active ? rwColors.primary : antGrey.grey5,
    });

    const onLoadMore = () => fetchIssues(filters.statusGroup, offset ?? 0);

    const isFetchingIssues = useIsLoading(FetchablePaginatedEntityType.Issues, appId);

    const onTabChange = (key: string) => {
        dispatch(setTriageStatusGroup({ appId, statusGroup: key as TriageStatusGroup }));
    };

    const { openCount, closedCount } = useSelector((state: RootState) => selectIssuesCounts(state, appId));

    return (
        <div style={{ display: 'flex', flexDirection: 'column', padding: '24px 32px 32px' }}>
            <Tabs
                className="triage-issues-tabs"
                activeKey={filters.statusGroup}
                onChange={onTabChange}
                items={[
                    {
                        key: 'open',
                        label: tabLabel('Open', openCount, filters.statusGroup === 'open'),
                    },
                    {
                        key: 'closed',
                        label: tabLabel('Closed', closedCount, filters.statusGroup === 'closed'),
                    },
                ]}
                tabBarExtraContent={{
                    right: (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            {!releaseId && (
                                <>
                                    <Button
                                        size="small"
                                        disabled={!liveVersion}
                                        onClick={() => toggleReleaseFilter('live')}
                                        icon={
                                            <RocketOutlined
                                                style={{
                                                    color:
                                                        filters.releaseFilter === 'live' ?
                                                            runwayColors.white
                                                        :   antGrey.grey6,
                                                }}
                                            />
                                        }
                                        style={releaseFilterButtonStyle(filters.releaseFilter === 'live')}
                                    >
                                        Live release
                                    </Button>
                                    <Button
                                        size="small"
                                        disabled={!nextVersion}
                                        onClick={() => toggleReleaseFilter('next')}
                                        icon={
                                            <ClockCircleOutlined
                                                style={{
                                                    color:
                                                        filters.releaseFilter === 'next' ?
                                                            runwayColors.white
                                                        :   antGrey.grey6,
                                                }}
                                            />
                                        }
                                        style={releaseFilterButtonStyle(filters.releaseFilter === 'next')}
                                    >
                                        Next release
                                    </Button>
                                    <Divider type="vertical" style={{ height: 40, borderColor: antGrey.grey4 }} />
                                </>
                            )}
                            <Button
                                icon={<CheckSquareOutlined style={filterButtonIconStyle} />}
                                style={filterButtonTextStyle}
                            >
                                Select
                            </Button>
                            <Dropdown
                                trigger={['click']}
                                menu={{
                                    items: SORT_OPTIONS.map((opt) => ({ key: opt.value, label: opt.label })),
                                    selectedKeys: [filters.sortBy],
                                    onClick: ({ key }) =>
                                        dispatch(setTriageSortBy({ appId, sortBy: key as TriageSortBy })),
                                }}
                            >
                                <Button
                                    icon={<AimOutlined style={filterButtonIconStyle} />}
                                    style={filterButtonTextStyle}
                                >
                                    Sort: {sortLabel(filters.sortBy)}
                                </Button>
                            </Dropdown>
                            <Badge
                                count={appliedFilterCount}
                                size="small"
                                style={{ backgroundColor: rwColors.primary }}
                            >
                                <Popover
                                    trigger="click"
                                    placement="bottomRight"
                                    destroyTooltipOnHide
                                    overlayInnerStyle={{ padding: 0, overflow: 'hidden' }}
                                    open={isFilterPopoverOpen}
                                    onOpenChange={setIsFilterPopoverOpen}
                                    content={
                                        <TriageFilters
                                            orgId={orgId}
                                            appId={appId}
                                            showVersionFilter={isVersionFilterActive}
                                            onApply={() => setIsFilterPopoverOpen(false)}
                                        />
                                    }
                                >
                                    <Button
                                        icon={<FilterOutlined style={filterButtonIconStyle} />}
                                        style={filterButtonTextStyle}
                                    >
                                        Filter
                                    </Button>
                                </Popover>
                            </Badge>
                        </div>
                    ),
                }}
            />
            <div
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 16,
                    marginTop: 8,
                }}
            >
                <div style={{ flex: '0 1 875px', minWidth: 0 }}>
                    <Input.Search
                        placeholder="Search"
                        enterButton={<Button icon={<SearchOutlined style={{ color: antGrey.grey7 }} />} />}
                        allowClear
                        value={searchInput}
                        onChange={(e) => onSearchChange(e.target.value)}
                        onSearch={commitSearch}
                        style={{ width: '100%' }}
                    />
                </div>
                <Button
                    icon={<PlusOutlined style={{ color: rwColors.primary }} />}
                    onClick={() => setIsCreateModalOpen(true)}
                    style={{
                        marginLeft: 'auto',
                        color: rwColors.primary,
                        backgroundColor: colorWithAlpha(rwColors.primary, 0.1),
                        borderColor: 'transparent',
                        paddingLeft: 32,
                        paddingRight: 32,
                        visibility: filters.statusGroup === 'open' ? 'visible' : 'hidden',
                    }}
                >
                    Add a new issue
                </Button>
            </div>
            {isFetchingIssues && (
                <div style={{ display: 'flex', justifyContent: 'center', paddingTop: 16 }}>
                    <Spin indicator={<LoadingOutlined style={{ fontSize: 18, color: rwColors.primary }} spin />} />
                </div>
            )}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12, paddingTop: 24 }}>
                {issues.map((issue) => (
                    <TriageIssueCard
                        key={issue.id}
                        issue={issue}
                        orgId={orgId}
                        viewType={releaseId ? TriageViewType.Release : TriageViewType.App}
                    />
                ))}
                {issues.length >= PAGE_SIZE && (
                    <Button onClick={onLoadMore} style={{ alignSelf: 'center' }}>
                        Load more
                    </Button>
                )}
            </div>
            <CreateIssueModal
                open={isCreateModalOpen}
                onClose={() => setIsCreateModalOpen(false)}
                orgId={orgId}
                appId={appId}
                lockedReleaseId={releaseId}
                statusGroup={filters.statusGroup}
                sources={filters.sources}
                sortBy={filters.sortBy}
            />
        </div>
    );
};
